import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { rankingApi } from '../api/seasons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

const CLASS_STYLE: Record<string, string> = {
  A: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  B: 'bg-blue-100 text-blue-800 border-blue-300',
  C: 'bg-gray-100 text-gray-600 border-gray-300',
};

type ClassFilter = 'ALL' | 'A' | 'B' | 'C';

export default function Ranking() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [classFilter, setClassFilter] = React.useState<ClassFilter>('ALL');

  const { data: ranking = [], isLoading } = useQuery({
    queryKey: ['ranking', classFilter],
    queryFn: () => rankingApi.list(classFilter === 'ALL' ? undefined : classFilter).then(r => r.data),
  });

  const { data: history = [] } = useQuery({
    queryKey: ['elo-history'],
    queryFn: () => rankingApi.myHistory().then(r => r.data),
    enabled: !!user,
  });

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('ranking.title')}</h1>
      <p className="text-gray-500 text-sm mb-6">{t('ranking.subtitle')}</p>

      {/* Class filter */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <button
          onClick={() => setClassFilter('ALL')}
          className={`badge border text-xs font-semibold transition-opacity ${
            classFilter === 'ALL' ? 'bg-primary-100 text-primary-800 border-primary-300' : 'bg-gray-100 text-gray-500 border-gray-300 opacity-60 hover:opacity-100'
          }`}
        >
          {t('ranking.all')}
        </button>
        {(['A','B','C'] as const).map(cls => (
          <button
            key={cls}
            onClick={() => setClassFilter(cls)}
            className={`badge border text-xs font-semibold transition-opacity ${CLASS_STYLE[cls]} ${
              classFilter !== 'ALL' && classFilter !== cls ? 'opacity-60 hover:opacity-100' : ''
            }`}
          >
            {t(`ranking.class${cls}`)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="card animate-pulse h-14" />
          ))}
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-gray-200 bg-gray-50">
                <th className="text-left py-3 px-4 text-gray-500 font-medium w-12">#</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">{t('ranking.player')}</th>
                <th className="text-center py-3 px-4 text-gray-500 font-medium w-20">{t('ranking.class')}</th>
                <th className="text-center py-3 px-4 text-gray-900 font-semibold w-24">ELO</th>
              </tr>
            </thead>
            <tbody>
              {ranking.map((p, i) => {
                const isMe = user?.id === p.id;
                return (
                  <tr key={p.id}
                    className={`border-b border-gray-100 last:border-0 ${isMe ? 'bg-primary-50' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                  >
                    <td className="py-3 px-4">
                      {i < 3 ? (
                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                          i === 0 ? 'bg-yellow-400 text-yellow-900' :
                          i === 1 ? 'bg-gray-300 text-gray-700' :
                          'bg-amber-600 text-white'
                        }`}>{p.rank}</span>
                      ) : (
                        <span className="text-gray-400 font-medium pl-1">{p.rank}</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <Link to={`/users/${p.id}`} className="font-medium text-gray-900 hover:text-primary-600">
                        {p.first_name} {p.last_name}
                        {isMe && <span className="ml-2 text-xs text-primary-600 font-semibold">{t('ranking.you')}</span>}
                      </Link>
                      {p.level && <span className="ml-2 text-xs text-gray-400">{p.level}</span>}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`badge border text-xs ${CLASS_STYLE[p.class]}`}>{p.class}</span>
                    </td>
                    <td className="py-3 px-4 text-center font-bold text-gray-900">{p.elo}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* My ELO history */}
      {user && history.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('ranking.myHistory')}</h2>
          <div className="space-y-2">
            {history.map(h => (
              <div key={h.id} className="flex items-center justify-between bg-white rounded-lg border border-gray-100 px-4 py-2 text-sm">
                <span className="text-gray-500">{new Date(h.created_at).toLocaleDateString('de-DE')}</span>
                <span className="text-gray-600">{t(`ranking.reason_${h.reason}`)}</span>
                <span className="font-mono text-gray-400">{h.elo_before} → {h.elo_after}</span>
                <span className={`font-bold ${h.change >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {h.change >= 0 ? '+' : ''}{h.change}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
